import api from "./api";
import {
  ListSatisfactionSurveyTemplatesResponse,
  SaveSatisfactionSurveyTemplatePayload,
  SatisfactionSurveySourceColumn,
  SatisfactionSurveySourceTable,
  SatisfactionSurveyQuestionResolvedOption,
  SatisfactionSurveyQuestionPayload,
  SatisfactionSurveyTemplate,
  UpdateSatisfactionSurveyTemplatePayload,
} from "../types/SatisfactionSurveyTemplate";

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("authToken");

  if (!token) {
    throw new Error("Token nao encontrado");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

export async function findAllSatisfactionSurveyTemplates(
  currentPage: number,
  perPage: number,
): Promise<ListSatisfactionSurveyTemplatesResponse> {
  const response = await api.get<ListSatisfactionSurveyTemplatesResponse>(
    "/satisfaction-survey-templates",
    {
      params: {
        page: currentPage,
        per_page: perPage,
      },
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export async function findSatisfactionSurveyTemplate(
  templateId: string | number,
): Promise<SatisfactionSurveyTemplate | null> {
  const response = await api.get<SatisfactionSurveyTemplate>(
    `/satisfaction-survey-templates/${templateId}`,
    {
      headers: getAuthHeaders(),
    },
  );

  return response.data || null;
}

export async function duplicateSatisfactionSurveyTemplate(
  templateId: string | number,
): Promise<SatisfactionSurveyTemplate | null> {
  const response = await api.post<SatisfactionSurveyTemplate>(
    `/satisfaction-survey-templates/${templateId}/duplicate`,
    {},
    {
      headers: getAuthHeaders(),
    },
  );

  return response.data || null;
}

export async function deleteSatisfactionSurveyTemplate(
  templateId: string | number,
): Promise<void> {
  await api.delete(`/satisfaction-survey-templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
}

export async function createSatisfactionSurveyTemplate(
  payload: SaveSatisfactionSurveyTemplatePayload,
): Promise<void> {
  await api.post("/satisfaction-survey-templates", payload, {
    headers: getAuthHeaders(),
  });
}

export async function updateSatisfactionSurveyTemplate(
  templateId: string | number,
  payload: UpdateSatisfactionSurveyTemplatePayload,
): Promise<void> {
  await api.put(`/satisfaction-survey-templates/${templateId}`, payload, {
    headers: getAuthHeaders(),
  });
}

export async function createSatisfactionSurveyQuestion(
  templateId: string | number,
  payload: SatisfactionSurveyQuestionPayload,
): Promise<void> {
  await api.post(`/satisfaction-survey-templates/${templateId}/questions`, payload, {
    headers: getAuthHeaders(),
  });
}

export async function updateSatisfactionSurveyQuestion(
  templateId: string | number,
  questionId: string | number,
  payload: SatisfactionSurveyQuestionPayload,
): Promise<void> {
  await api.put(
    `/satisfaction-survey-templates/${templateId}/questions/${questionId}`,
    payload,
    {
      headers: getAuthHeaders(),
    },
  );
}

export async function deleteSatisfactionSurveyQuestion(
  templateId: string | number,
  questionId: string | number,
): Promise<void> {
  await api.delete(
    `/satisfaction-survey-templates/${templateId}/questions/${questionId}`,
    {
      headers: getAuthHeaders(),
    },
  );
}

export async function findSatisfactionSurveyQuestionOptions(
  templateId: string | number,
  questionId: string | number,
): Promise<SatisfactionSurveyQuestionResolvedOption[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      question_id: number;
      source: string;
      options: SatisfactionSurveyQuestionResolvedOption[];
    };
  }>(
    `/satisfaction-survey-templates/${templateId}/questions/${questionId}/options`,
    {
      headers: getAuthHeaders(),
    },
  );

  return response.data.data?.options || [];
}

export async function findSatisfactionSurveySourceColumns(
  sourceTable: SatisfactionSurveySourceTable,
): Promise<SatisfactionSurveySourceColumn[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      source: SatisfactionSurveySourceTable;
      columns: SatisfactionSurveySourceColumn[];
    };
  }>(`/satisfaction-survey-sources/${sourceTable}/columns`, {
    headers: getAuthHeaders(),
  });

  return response.data.data?.columns || [];
}
